import { useEffect } from 'react';

import { useDebouncedState, useUpdateObjectClassMutation } from '@/hook';
import {
  KeyboardArrowDown,
  Plus,
  VFlex,
  VIconButton,
  VInput,
  VSwitch,
  cn,
} from '@/package';
import type { ObjectClass } from '@/type';

import { Icon } from '../IconPopover';

const SuperClassItem = ({
  superClass,
  isExtendClassOpen,
  setIsExtendClassOpen,
  setIsNewExtendClassItemFormOpen,
}: {
  superClass: ObjectClass;
  isExtendClassOpen: boolean;
  setIsExtendClassOpen: (isExtendClassOpen: boolean) => void;
  setIsNewExtendClassItemFormOpen: (
    isNewExtendClassItemFormOpen: boolean,
  ) => void;
}) => {
  const { extendClass } = superClass;
  const isExtendClassExist = extendClass && extendClass?.length > 0;
  const { mutate: updateObjectClass } = useUpdateObjectClassMutation();
  const superClassAlias = superClass.alias || superClass.name;
  const {
    state: alias,
    debouncedState: debouncedAlias,
    onChange: onAliasChange,
  } = useDebouncedState(superClassAlias);

  useEffect(() => {
    if (superClassAlias === debouncedAlias || !debouncedAlias.length) return;

    updateObjectClass({
      pObjectClassID: superClass.id,
      ...superClass,
      alias: debouncedAlias,
    });
  }, [debouncedAlias]);

  return (
    <VFlex className="h-8 w-full divide-x divide-gray-300 text-body-md text-black">
      <VFlex className="h-full w-8 p-2" justify="center" items="start">
        <KeyboardArrowDown
          className={cn('size-4 text-primary-500', {
            'rotate-180': isExtendClassOpen,
            'cursor-pointer': isExtendClassExist,
            'cursor-default': !isExtendClassExist,
          })}
          role="button"
          onClick={() =>
            isExtendClassExist && setIsExtendClassOpen(!isExtendClassOpen)
          }
        />
      </VFlex>
      <VFlex className="w-10" items="center" justify="center">
        <Icon data={superClass.icon?.data ?? ''} color={superClass.color} />
      </VFlex>
      <VFlex
        className="w-[184px] px-2"
        items="center"
        justify="between"
        gap={0.25}
      >
        <VInput
          size="sm"
          orientation="horizontal"
          value={alias}
          valid={alias.length > 0}
          onChange={e => onAliasChange(e.target.value)}
        />

        {superClass.classType === 'Super' && (
          <VIconButton
            Icon={Plus}
            size="sm"
            variant="roundPrimary"
            onClick={() => setIsNewExtendClassItemFormOpen(true)}
          />
        )}
      </VFlex>
      <VFlex
        className="w-[231px] px-2 py-1"
        gap={0.25}
        items="center"
        justify="center"
      >
        {superClass.size.map((size, index) => (
          <VInput
            key={`${size}-${index}`}
            size="sm"
            orientation="horizontal"
            fluid
            value={size}
            label={index === 0 ? 'L' : index === 1 ? 'W' : 'D'}
            readOnly
          />
        ))}
      </VFlex>
      <VFlex className="flex-1 px-2" items="center" justify="center">
        <VInput
          fluid
          size="sm"
          orientation="horizontal"
          readOnly
          value={superClass.memo}
        />
      </VFlex>
      <VFlex className="w-[72px]" items="center" justify="center">
        <VSwitch checked={superClass.enable} disabled />
      </VFlex>
    </VFlex>
  );
};

export default SuperClassItem;
